import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

const CATEGORIES = [
  { value: 'general', label: 'General Question' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing & Account' },
  { value: 'feature_request', label: 'Feature Request' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', description: 'Not urgent, can wait' },
  { value: 'medium', label: 'Medium', description: 'Affecting my work' },
  { value: 'high', label: 'High', description: 'Blocking important tasks' },
  { value: 'urgent', label: 'Urgent', description: 'Critical business impact' }
];

export default function SubmitTicketModal({ isOpen, onClose, onSuccess }) {
  const getCurrentCompanyId = useAuthStore(state => state.getCurrentCompanyId);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: '',
    errorDetails: {
      errorMessage: '',
      stepsToReproduce: '',
      browserInfo: '',
      pageUrl: ''
    }
  });
  
  // Auto-capture browser info when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        errorDetails: {
          ...prev.errorDetails,
          browserInfo: `${navigator.userAgent}`,
          pageUrl: window.location.href
        }
      }));
    }
  }, [isOpen]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleErrorDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      errorDetails: { ...prev.errorDetails, [field]: value }
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in subject and description');
      return;
    }
    
    setLoading(true);
    try {
      const companyId = getCurrentCompanyId();
      const response = await apiHelpers.createTicket({ ...formData, companyId });
      onSuccess(response.data.ticket);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      subject: '',
      category: 'general',
      priority: 'medium',
      description: '',
      errorDetails: {
        errorMessage: '',
        stepsToReproduce: '',
        browserInfo: '',
        pageUrl: ''
      }
    });
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  const isTechnical = formData.category === 'technical';
  
  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white">
              Submit Support Ticket
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-200"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Subject *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Brief summary of your issue"
                className="input w-full"
                maxLength={200}
                required
              />
            </div>
            
            {/* Category & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="input w-full"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="input w-full"
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label} - {p.description}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your issue or question in detail..."
                className="input w-full h-32 resize-none"
                required
              />
            </div>
            
            {/* Technical Details (conditional) */}
            {isTechnical && (
              <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <ExclamationTriangleIcon className="w-5 h-5" />
                  <span className="font-medium">Technical Issue Details</span>
                </div>
                
                {/* Error Message */}
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                    Error Message (if any)
                  </label>
                  <textarea
                    value={formData.errorDetails.errorMessage}
                    onChange={(e) => handleErrorDetailsChange('errorMessage', e.target.value)}
                    placeholder="Paste any error messages you see..."
                    className="input w-full h-20 resize-none font-mono text-sm"
                  />
                </div>
                
                {/* Steps to Reproduce */}
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                    Steps to Reproduce
                  </label>
                  <textarea
                    value={formData.errorDetails.stepsToReproduce}
                    onChange={(e) => handleErrorDetailsChange('stepsToReproduce', e.target.value)}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. Error appears..."
                    className="input w-full h-24 resize-none"
                  />
                </div>
                
                {/* Browser Info (auto-captured) */}
                <div className="flex items-center gap-2 text-xs text-surface-500">
                  <ComputerDesktopIcon className="w-4 h-4" />
                  <span>Browser info will be automatically included</span>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-surface-200 dark:border-surface-700">
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Ticket'
                )}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
